/**
 * variant-auto-select.js
 *
 * Automatically selects the first available combination of sub-variants
 * (option 2+) whenever the page loads or option 1 changes.
 *
 * Works with Dawn's HTMLUpdateUtility.viewTransition DOM-swap pattern by
 * using a MutationObserver rather than relying on inline script timing.
 */
(function () {

  // ─── Helpers ────────────────────────────────────────────────────────────────

  function getVariantsData(vs) {
    try {
      var el = vs.querySelector('[data-product-variants]');
      return el ? JSON.parse(el.textContent) : [];
    } catch (e) {
      return [];
    }
  }

  /**
   * Returns the array of currently-selected option STRING values in option
   * order (selects first, then pill fieldsets), optionally replacing the
   * value at one fieldset index with a candidate value.
   */
  function getOptionValues(vs, overrideFieldsetIdx, overrideVal) {
    var fieldsetsSeen = 0;
    return Array.from(vs.querySelectorAll('select, fieldset.product-form__input--pill')).map(function (el) {
      if (el.tagName === 'SELECT') return el.value;
      var fi = fieldsetsSeen++;
      if (fi === overrideFieldsetIdx) return overrideVal;
      var checked = el.querySelector('input[type="radio"]:checked');
      return checked ? checked.value : null;
    });
  }

  /**
   * Returns true if candidateVal at fieldset index fi forms an available
   * variant with the current selections of all other options.
   */
  function isCombinationAvailable(vs, variantsData, fi, candidateVal) {
    if (!variantsData.length) return true;
    var vals = getOptionValues(vs, fi, candidateVal);
    return variantsData.some(function (v) {
      if (!v.available) return false;
      return ['option1', 'option2', 'option3'].every(function (key, i) {
        return vals[i] == null || v[key] === vals[i];
      });
    });
  }

  // ─── Core auto-select ────────────────────────────────────────────────────────

  function autoSelectForElement(vs) {
    var variantsData = getVariantsData(vs);
    var fieldsets = Array.from(vs.querySelectorAll('fieldset.product-form__input--pill'));

    fieldsets.forEach(function (fieldset, fi) {
      var radios = Array.from(fieldset.querySelectorAll('input[type="radio"]'));
      var checked = radios.find(function (r) { return r.checked; });

      // Nothing to do if the current selection is already a valid available combo
      if (checked && isCombinationAvailable(vs, variantsData, fi, checked.value)) return;

      // Uncheck all then find the first radio that forms an available combo
      radios.forEach(function (r) { r.checked = false; });
      var best = radios.find(function (r) {
        return isCombinationAvailable(vs, variantsData, fi, r.value);
      }) || radios[0];

      if (best) {
        best.checked = true;
        best.setAttribute('checked', '');
        best.dispatchEvent(new Event('change', { bubbles: true }));
      }
    });
  }

  function initAll(container) {
    (container || document).querySelectorAll('variant-selects[data-section]').forEach(autoSelectForElement);
  }

  // ─── MutationObserver – catches Dawn's DOM swaps ─────────────────────────────

  var observer = new MutationObserver(function (mutations) {
    mutations.forEach(function (mutation) {
      mutation.addedNodes.forEach(function (node) {
        if (!node || node.nodeType !== 1) return;
        // Direct match (viewTransition inserts variant-selects itself)
        if (node.tagName && node.tagName.toLowerCase() === 'variant-selects') {
          autoSelectForElement(node);
        } else if (typeof node.querySelectorAll === 'function') {
          node.querySelectorAll('variant-selects[data-section]').forEach(autoSelectForElement);
        }
      });
    });
  });

  // ─── Capture-phase handler – fires before Dawn reads selectedOptionValues ────

  if (!document.__ctdVariantResetBound) {
    document.__ctdVariantResetBound = true;
    document.addEventListener('change', function (e) {
      var changed = e.target;
      var variantSelects = changed.closest('variant-selects');
      if (!variantSelects) return;

      var allSelects   = Array.from(variantSelects.querySelectorAll('select'));
      var allFieldsets = Array.from(variantSelects.querySelectorAll('fieldset.product-form__input--pill'));

      var isFirstSelect = changed.tagName === 'SELECT' && allSelects.indexOf(changed) === 0;
      // isFirstRadio is only true when option 1 itself is a radio (no selects exist).
      // If a dropdown is present it owns option 1, so the first fieldset is option 2+.
      var isFirstRadio  = changed.tagName === 'INPUT'  && changed.type === 'radio' &&
                          allFieldsets.length > 0 && allFieldsets[0].contains(changed) &&
                          allSelects.length === 0;

      if (!isFirstSelect && !isFirstRadio) return;

      // Reset subsequent selects to their first option
      allSelects.slice(isFirstSelect ? 1 : 0).forEach(function (sel) {
        var first = sel.querySelector('option');
        if (!first) return;
        sel.value = first.value;
        var prev = sel.querySelector('option[selected]');
        if (prev) prev.removeAttribute('selected');
        first.setAttribute('selected', 'selected');
      });

      // Reset subsequent radio fieldsets – set a best-effort first radio so
      // Dawn's fetch URL includes an option2 value; autoSelectForElement will
      // correct it via the MutationObserver once the new DOM is inserted.
      allFieldsets.slice(isFirstRadio ? 1 : 0).forEach(function (fs) {
        var radios = Array.from(fs.querySelectorAll('input[type="radio"]'));
        if (!radios.length) return;
        radios.forEach(function (r) { r.checked = false; });
        var first = radios.find(function (r) { return !r.classList.contains('disabled'); }) || radios[0];
        if (first) first.checked = true;
      });
    }, true); // capture phase – runs before Dawn's bubble listener
  }

  // ─── Boot ────────────────────────────────────────────────────────────────────

  function boot() {
    initAll();
    observer.observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

})();
